import type { CoverRedoRun, CoverRedoState } from 'fym-shared/src/music24/coverRedo';

/** True for an answer of the cover redo route (an older API answers 404, a test server `{}`). Pure. */
export function isCoverRedoState(value: unknown): value is CoverRedoState {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const answer = value as Partial<CoverRedoState>;
  return typeof answer.outOfDate === 'boolean' && Array.isArray(answer.items) && typeof answer.cover === 'object';
}

/** The end card of a run shows for a day (design kit-redo-cover c2); an older run is history. */
export const COVER_REDO_END_CARD_MS = 24 * 60 * 60 * 1000;

/** What the cover redo banner of the Kit tab shows (design kit-redo-cover a, c). */
export type CoverRedoBannerState = 'hidden' | 'offer' | 'working' | 'done' | 'failed';

/** The parts of a redo answer that the banner reads: the cover redo and the date redo share them. */
export interface RedoBannerInput {
  outOfDate: boolean;
  run: CoverRedoRun | null;
}

/**
 * The banner state of a redo (the cover redo, the date redo). Pure.
 * - A redo that runs shows its progress (also after "Not now").
 * - The end of the newest run shows once: done, or failed when a file of the run failed. Closing
 *   it stores the run (`seenRun`); a run older than a day shows no end card. A failed run with files still out of date offers "Try again".
 * - Else the offer shows while files are out of date and the user did not put away the offer of
 *   this change (`dismissed` is the `offerKey` of the change that the user put away: the cover URL,
 *   the release date).
 */
export function redoBannerState(
  state: RedoBannerInput | undefined,
  offerKey: string,
  memory: { dismissed: string | null; seenRun: string | null },
  now: number = Date.now()
): CoverRedoBannerState {
  if (state === undefined) {
    return 'hidden';
  }
  const { run } = state;
  if (run !== null && run.state === 'working') {
    return 'working';
  }
  const isRecent = run !== null && now - Date.parse(run.startedAt) < COVER_REDO_END_CARD_MS;
  if (run !== null && isRecent && run.runId !== memory.seenRun) {
    return run.state === 'failed' ? 'failed' : 'done';
  }
  if (!state.outOfDate) {
    return 'hidden';
  }
  return memory.dismissed === offerKey ? 'hidden' : 'offer';
}

/** The banner state of the cover redo: the offer key is the cover of the song now. Pure. */
export function coverRedoBannerState(
  state: CoverRedoState | undefined,
  memory: { dismissedCover: string | null; seenRun: string | null },
  now: number = Date.now()
): CoverRedoBannerState {
  return redoBannerState(
    state,
    state?.cover.currentUrl ?? '',
    { dismissed: memory.dismissedCover, seenRun: memory.seenRun },
    now
  );
}
